<template>
  <div class="flex flex-col mb-2 max-w-4xl mx-auto" v-if="userProfile">
    <div class="flex flex-col sm:flex-row gap-1 sm:gap-2 items-center">
      <div class="shrink-0 grow-0">
        <UserImage :image-id="userProfile.profileImageId" :size="'lg'" />
      </div>
      <div class="sm:col-span-2 flex flex-col pb-2 sm:p-2 gap-2">
        <div class="flex items-center sm:mt-3 gap-2">
          <div>
            <p>
              {{ $t("userprofile.created_on") }}
              {{ $d(userProfile.createdOn, "short") }}
            </p>
            <h1>
              {{ userProfile.userName }}
            </h1>
            <p v-if="userProfile.verified">{{ $t("userprofile.verified") }}</p>
          </div>
          <img
            v-if="userProfile.verified"
            class="h-[54px]"
            src="~assets/icon/verified.svg"
            :alt="i18n.t('accessibility.verified')"
          />
        </div>
        <div v-if="own && auth.user.value?.export" class="flex flex-col items-start gap-1">
          <div class="flex items-center gap-1">
            <p>{{ $t("premium.export_text") }}</p>
            <ButtonPrimary :small="true" @click="downloadCSV">.csv
            </ButtonPrimary>
            <ButtonPrimary :small="true" @click="downloadJSON">.json</ButtonPrimary>
            <ButtonPrimary :small="true" @click="downloadExcel">.xlsx</ButtonPrimary>

          </div>
          <div class="flex items-center gap-1">
            <label for="plzs">{{ $t("premium.export_plzs") }}:</label>
            <input class="p-0.5" type="text" id="plzs" placeholder="8000,8001,8003" />
            <ButtonPrimary :small="true" @click="downloadPLZCSV">.csv</ButtonPrimary>
            <ButtonPrimary :small="true" @click="downloadPLZJSON">.json</ButtonPrimary>
            <ButtonPrimary :small="true" @click="downloadPLZExcel">.xlsx</ButtonPrimary>
          </div>
        </div>
        <div v-if="userProfile.userProfileStatistics" class="grid grid-cols-1 sm:grid-cols-3 text-center">
          <div
            class="p-1 border-theme-secondary/30 border-solid border-x-[1px] border-t-[1px] sm:border-r-0 sm:border-b-[1px] flex-col items-center"
          >
            <div>
              {{ userProfile?.userProfileStatistics.spots }}
            </div>
            <div>{{ $t("userprofile.numberOfSpots") }}</div>
          </div>
          <div
            class="p-1 border-theme-secondary/30 border-solid border-x-[1px] border-t-[1px] sm:border-r-0 sm:border-b-[1px] flex-col items-center"
          >
            <div>{{ userProfile?.userProfileStatistics.votes }}</div>
            <div>{{ $t("userprofile.numberOfVotes") }}</div>
          </div>
          <div
            class="p-1 border-theme-secondary/30 border-solid border-[1px] flex-col items-center"
          >
            <div>{{ userProfile?.userProfileStatistics.comments }}</div>
            <div>{{ $t("userprofile.numberOfComments") }}</div>
          </div>
        </div>
        <div
          v-if="userProfile.bio && userProfile.bio != ''"
          class="mt-3"
        >
          <h3 class="mb-1">
            {{ $t("userprofile.bio_title") }}
          </h3>
          <p
            id="user-profile-bio"
            v-linkify:options="{
            target: '_blank',
          }"
            v-html="textHelpers.replaceNewlineWithHtmlTags(userProfile.bio)"
          >
          </p>
        </div>
      </div>
    </div>
    <div v-if="userProfile.verifiedUserProfileStatistics" class="flex flex-col gap-3 mt-3">
      <div><h1>{{ $t("userprofile.statistics_title") }}</h1></div>
      <div class="flex items-center gap-1"><img src="~assets/icon/thumb-down-round-fixed.svg"
                                                :alt="i18n.t('userprofile.statistics_fixed_spots')" />
        <h3>{{ $t("userprofile.statistics_fixed_spots") }}:
          {{ userProfile.verifiedUserProfileStatistics.numberOfFixedSpots }}</h3></div>
<!--      <div class="flex flex-col sm:flex-row gap-3">-->
<!--        <div class="flex flex-col gap-2">-->
<!--          <h2>{{ $t("userprofile.statistics_spots_per_category") }}</h2>-->
<!--          <div class="max-w-[400px] max-h-[250px]">-->
<!--            <UserProfileCategoriesGraph :data="userProfile.verifiedUserProfileStatistics.numberOfSpotsPerCategory" />-->
<!--          </div>-->
<!--        </div>-->
<!--        <div class="flex flex-col gap-2 grow">-->
<!--          <h2>{{ $t("userprofile.statistics_spots_per_month") }}</h2>-->
<!--          <div class=" h-[250px]">-->
<!--            <UserProfileMonthsGraph :data="userProfile.verifiedUserProfileStatistics.numberOfSpotsCreatedPerMonth" />-->
<!--          </div>-->
<!--        </div>-->
<!--      </div>-->
    </div>
  </div>
</template>
<script setup lang="ts">

const i18n = useI18n();
const auth = useAuth();
defineProps<{ userProfile: UserProfile | null, own: boolean }>();

function downloadCSV() {
  useApiAuth().get<BlobPart>("export/allSpots/csv").then((response) => {
    const blob = new Blob([response], { type: "text/csv;charset=utf8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "export";
    link.click();
    URL.revokeObjectURL(link.href);
  });
}

function downloadJSON() {
  useApiAuth().get<BlobPart>("export/allSpots/json").then((response) => {
    const blob = new Blob([response], { type: "application/json;charset=utf8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "export";
    link.click();
    URL.revokeObjectURL(link.href);
  });
}

function downloadExcel() {
  useApiAuth().get<BlobPart>("export/allSpots/xlsx").then((response) => {
    const blob = new Blob([response], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "export";
    link.click();
    URL.revokeObjectURL(link.href);
  });
}

function getPlzInput(): Array<String> | undefined {
  const plzs = document.getElementById("plzs") as HTMLInputElement;
  const isValid = /^[0-9,.]*$/.test(plzs.value);
  if (!isValid) {
    alert("Please enter a valid PLZ list");
    return;
  }
  return plzs.value.split(",");
}

function plzsAvailable(plzs: String[] | undefined): boolean {
  return plzs !== undefined && plzs.length > 0 && plzs[0] !== "";
}

function downloadPLZCSV() {
  const plzs = getPlzInput();
  if (!plzsAvailable(plzs)) {
    return;
  }
  useApiAuth().get<BlobPart>("export/plzs/csv", { plzs: plzs }).then((response) => {
    const blob = new Blob([response], { type: "text/csv;charset=utf8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "export";
    link.click();
    URL.revokeObjectURL(link.href);
  });
}

function downloadPLZJSON() {
  const plzs = getPlzInput();
  if (!plzsAvailable(plzs)) {
    return;
  }
  useApiAuth().get<BlobPart>("export/plzs/json", { plzs: plzs }).then((response) => {
    const blob = new Blob([response], { type: "application/json;charset=utf8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "export";
    link.click();
    URL.revokeObjectURL(link.href);
  });
}

function downloadPLZExcel() {
  const plzs = getPlzInput();
  if (!plzsAvailable(plzs)) {
    return;
  }
  useApiAuth().get<BlobPart>("export/plzs/xlsx", { plzs: plzs }).then((response) => {
    const blob = new Blob([response], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "export";
    link.click();
    URL.revokeObjectURL(link.href);
  });
}

</script>
<style scoped lang="scss">

::v-deep(#user-profile-bio) {
  a {
    @apply text-theme-primary;
  }
}

</style>
